import { useTranslation } from 'react-i18next';

import { normaliseLocale } from './direction';

/**
 * Picks the reader's language out of a clinic-entered bilingual name.
 *
 * Clinic content is not translated by us — a clinic types its own service and branch names, in
 * Arabic, in English, or in both (ASM-005). Both fields are optional and either one is enough, so
 * displaying one is always a choice between "the language being read", "the other language" and
 * "nothing".
 *
 * The rule is: the reader's language, then the other one, then whatever identifier the caller
 * offers, then an em dash. Falling back to the other language rather than to the dash matters —
 * a receptionist reading the English interface would rather see the Arabic name of a branch than
 * a row that says nothing.
 *
 * This exists as one function because it was previously written out at each of thirteen call
 * sites as `nameAr ?? nameEn`, which is not a fallback at all: it is Arabic-first regardless of
 * who is reading, and it made the entire English interface list its branches, rooms, services,
 * packages, offers and tenants in Arabic.
 */
export interface BilingualName {
  readonly nameAr?: string | null;
  readonly nameEn?: string | null;
}

export interface BilingualDisplayName {
  readonly displayNameAr?: string | null;
  readonly displayNameEn?: string | null;
}

const present = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

export function pickLocalisedName(
  arabic: string | null | undefined,
  english: string | null | undefined,
  language: string,
  fallback?: string | null,
): string {
  const preferred = normaliseLocale(language) === 'ar' ? [arabic, english] : [english, arabic];
  return preferred.find(present) ?? (present(fallback) ? fallback : '—');
}

/**
 * The hook form, for components. Returns a function rather than a value so one call covers a whole
 * table without a hook per row.
 */
export function useLocalisedName(): {
  name: (value: BilingualName, fallback?: string | null) => string;
  displayName: (value: BilingualDisplayName, fallback?: string | null) => string;
} {
  const { i18n } = useTranslation();
  const language = i18n.language;

  return {
    name: (value, fallback) => pickLocalisedName(value.nameAr, value.nameEn, language, fallback),
    displayName: (value, fallback) =>
      pickLocalisedName(value.displayNameAr, value.displayNameEn, language, fallback),
  };
}
